import logo from "@assets/logo-blue.png";
import samHappy from "@assets/champions/sam_happy.png";
import { DialogM } from "@common/components/alert/dialog/Dialog";
import FilledButtonM from "@common/components/button/FilledButtonM";
import { IconImageM } from "@common/components/icons/IconsM";
import { ModalM } from "@common/components/modal/ModalM";
import { PlaySoundM } from "@common/components/playSound/PlaySoundM";
import TextM from "@common/components/text/TextM";
import TitleM from "@common/components/text/TitleM";
import { HeaderContainerM } from "@common/header/HeaderContainerM";
import InformationBarComponent from "@common/livekit/components/InformationBarComponent";
import DownloadAppCard from "@common/liveMode/DownloadAppCard";
import BrowserChoiceScreen from "@common/liveMode/precall/BrowserChoiceScreen";
import PreCallCheckBoundary from "@common/liveMode/precall/PreCallCheckBoundary";
import PreCallCheckPanel from "@common/liveMode/precall/PreCallCheckPanel";
import {
    alertCategory,
    materialBilanForMemory,
    shouldAlertTechnician,
} from "../../engine/precall/deviceCheckRules";
import SamConversationBase from "@common/liveMode/SamConversationBase";
import { theme } from "@common/theme/theme";
import {
    DimensionsContext,
    IS_WEB,
    isChrome,
    isWebMobile,
} from "@common/utils/mobileUtils";
import { apiIdle, apiRequest } from "@data/redux/actions/api";
import {
    setCallPortal,
    setIsLiveModeEnabled,
    setIssoundMode,
    setPrecallDevices,
    setSuggestedActivity,
} from "@data/redux/actions/liveMode"
import {
    forgetDeviceCheck,
    rememberDeviceCheck,
} from "@data/redux/actions/precallMemory"
import { setMuteAudio, setMuteVideo } from "@data/redux/actions/videoCall"
import { useNavigation } from "@react-navigation/native"
import {
    detectStore,
    openStoreUrl,
    resolveStoreUrl,
    trackStoreClick,
} from "@common/utils/storeLinks"
import React, {useContext, useEffect, useRef, useState} from "react"
import { ActivityIndicator, Platform, ScrollView, StyleSheet, TouchableOpacity, View, Image } from "react-native"
import Animated, { FadeIn, FadeOut, SlideInUp, SlideOutUp, BounceIn } from "react-native-reanimated"
import { LinearGradient } from "expo-linear-gradient"
import { IconButton } from "react-native-paper"
import { connect } from "react-redux"
import { authService } from "../../data/services/authService"
import AvatarGroupM from "../components/avatar/AvatarGroupM"
import { onAvatarPress } from "../livekit/components-web/StudentControlBar"
import { liveService } from "@data/services/liveService"
import { liveServiceV2 } from "@data/services/liveServiceV2"
import { useApiStatus } from "@common/hooks/useApiStatus"
import {formIds} from "@data/constants/formConstants";
import {
    ISSUE_REPORT_CATEGORY,
    ISSUE_REPORT_SOURCE,
} from "@data/constants/issueReportConstants";
import * as amplitude from "@amplitude/analytics-react-native";
import {hasRole} from "@data/utility/ability";
import { hasFeature } from "@data/utility/Utils";
import LegacyPreCallCheck from "@common/liveMode/precall/LegacyPreCallCheck";
import { PRECALL_EVENT, trackPrecall } from "../../engine/precall/precallTracking";
import { browserFamily } from "../../engine/precall/verifiedMemory";
import { AUDIENCE, audienceCopy } from "../../engine/precall/deviceCheckCopy";
import { useSafeAreaInsets } from "react-native-safe-area-context";

const PreCallScreen = (props) => {
    const liveSessionId = props.route?.params?.liveSessionId ?? "0"

    const dimension = useContext(DimensionsContext)
    const liveSession = props.liveSessions?.find(
        (x) => x?.id === parseInt(liveSessionId)
    )
    const navigation = useNavigation()

    const [isModalOpen, setIsModalOpen] = useState(false)
    // Chrome sur ordinateur passe directement au test ; ailleurs, l'écran de
    // recommandation s'interpose, avec sa sortie « Continuer sur ce navigateur ».
    const [showJoinScreen, setShowJoinScreen] = useState(
        !IS_WEB || (isChrome() && !isWebMobile())
    )
    const [isModalVisible, setIsModalVisible] = useState(false)
    const [conversation, setConversation] = useState(null)
    const [onGoingConversation, setOnGoingConversation] = useState(false)
    const [checkpointDismissed, setCheckpointDismissed] = useState(false) // Once closed, don't reopen
    const [showSamOverlay, setShowSamOverlay] = useState(false)

    // Ce que l'écran a besoin de savoir du test matériel, et rien d'autre :
    // les quatre verdicts décident si l'équipe est prévenue, les essais
    // nourrissent le signalement.
    // Aucun verdict au départ, et pas un micro « en attente » : la règle
    // ignore les étapes absentes du bilan, et nommer le micro ici désignerait
    // encore un appareil comme celui qui compte.
    const [check, setCheck] = useState({ attempts: [], allDone: false })

    /*
     * Le test guidé est ouvert à toutes les salles depuis le 16/09.
     *
     * Il s'est ouvert salle par salle pendant une journée (drapeau
     * `precall-check` dans `live_room.version`, 74 salles le 15/09) : 101
     * entrées, aucune erreur, et le seul réglage nécessaire portait sur les
     * alertes, côté serveur. Le drapeau n'est plus lu ; l'écran d'avant ne
     * reste que le repli d'une séance introuvable.
     */
    /*
     * Tant que la séance n'est pas chargée, on ne sait pas quel écran servir.
     * Juste après la connexion, ou au rechargement de la page, `live_sessions`
     * arrive avec l'utilisateur, une seconde après le montage : décider sur
     * `undefined` servait l'écran d'avant, que le test guidé remplaçait
     * ensuite. L'élève voyait l'un, puis l'autre.
     */
    const sessionLoaded = !!liveSession

    /*
     * Arrivé par un lien direct puis connecté, l'élève a un utilisateur sans
     * ses séances : la bascule sur le jeton famille remplace l'objet, et c'est
     * l'accueil — jamais affiché ici — qui les rechargeait. On les demande
     * donc nous-mêmes, une fois.
     */
    const askedUserRef = useRef(false)
    useEffect(() => {
        if (sessionLoaded || askedUserRef.current || !props.user?.id) return
        askedUserRef.current = true
        props.apiRequest(authService.getUser)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionLoaded, props.user?.id])

    // Et si la séance n'arrive pas, on n'attend pas indéfiniment : sans
    // séance, l'écran d'avant, qui n'a besoin de rien pour s'afficher.
    const [sessionTimedOut, setSessionTimedOut] = useState(false)
    useEffect(() => {
        if (sessionLoaded) return undefined
        const timer = setTimeout(() => setSessionTimedOut(true), 6000)
        return () => clearTimeout(timer)
    }, [sessionLoaded])
    const sessionKnown = sessionLoaded || sessionTimedOut

    const guidedCheck = sessionLoaded
    // Sans test guidé (séance introuvable), il n'y a rien à finir : le bouton
    // d'entrée est plein d'emblée.
    const allDone = sessionKnown && (!guidedCheck || check.allDone)

    /** Ce que chaque événement Amplitude du test porte, quel que soit l'appareil. */
    const tracking = {
        live_session_id: liveSessionId,
        platform: IS_WEB ? "web" : Platform.OS,
        browser: IS_WEB && typeof navigator !== "undefined" ? browserFamily(navigator.userAgent) : Platform.OS,
    }

    // Assez large pour qu'un apercu 16:9 soit regardable, assez etroit pour
    // que le texte reste lisible d'un seul balayage. 468 px etait la largeur
    // d'un telephone posee sur un ecran d'ordinateur : la carte flottait au
    // milieu du vide et l'apercu camera etait minuscule.
    const columnWidth = Math.min(
        IS_WEB ? dimension.width - 48 : dimension?.width - 20,
        620
    )
    // Partagé par le test et par son filet de secours, pour que la carte
    // occupe la même place dans les deux cas.
    const checkPanelStyle = { width: columnWidth, alignSelf: "center", marginTop: 14 }

    const hasReportedRef = useRef(false)

    /**
     * Sam ne paraît qu'avec le bouton d'entrée, une fois le test terminé.
     *
     * Pendant le test, l'entrée n'est qu'un lien discret — « entrer sans finir
     * le test ». Une mascotte joyeuse posée au-dessus revenait à encourager
     * l'élève à sauter la vérification, et elle lui passait littéralement sur
     * le texte.
     */
    const showSam =
        !props.createConversationRequest &&
        !props.getStudentPreCallDataRequest &&
        hasRole(props.user, "student") &&
        !!conversation &&
        allDone

    const insets = useSafeAreaInsets()

    const hasRequestedCheckpointRef = useRef(false)

    const isStartingBlockCheckpointEnabled = hasFeature(liveSession?.live_room, "checkpoint-sb")

    const handleShowModal = () => {
        setIsModalOpen(!isModalOpen)
    }

    const joinSession = () => {
        props.apiIdle(liveService.createConversation)

        props.setIsLiveModeEnabled(true, liveSessionId)
        props.apiRequest(authService.getUser)
    }

    const proceedToJoin = () => {
        if (hasRole(props.user, "student") && isStartingBlockCheckpointEnabled && conversation && !checkpointDismissed) {
            // Show Sam overlay instead of navigating
            setShowSamOverlay(true)
        } else {
            joinSession()
        }
    }

    /**
     * L'élève entre alors qu'un de ses voyants n'est pas au vert : on prévient
     * l'équipe technique à sa place, avec ce qu'il a déjà essayé — et **sans le
     * lui annoncer**. Le signalement est une information d'équipe, pas une promesse
     * d'aide : la tenir supposerait une astreinte sur le canal, soirs et
     * week-ends compris.
     *
     * Il ne part qu'une fois — le backend déduplique aussi de son côté, pour
     * qu'un aller-retour hors du live ne re-pingue personne.
     */
    const alertTechnician = () => {
        if (hasReportedRef.current) return
        hasReportedRef.current = true

        /*
         * Le souvenir ne survit pas à un appareil en panne — mais le réseau
         * n'est pas un appareil.
         *
         * Cet élève entre avec du matériel dont on sait qu'il ne marche pas,
         * alors que son empreinte de configuration, elle, n'a pas bougé — rien
         * dans la liste des périphériques ne trahit un casque mort. C'est le
         * seul endroit où l'on en sait plus qu'elle : on efface, pour que la
         * prochaine entrée repasse par le parcours complet.
         *
         * Un Wi-Fi capricieux, lui, ne dit rien de la configuration. L'effacer
         * renvoyait au parcours complet, un soir de mauvaise connexion,
         * précisément les élèves à qui le raccourci sert le plus. La règle sait
         * ignorer les étapes absentes du bilan : on ne lui passe donc que les
         * trois qui décrivent le matériel.
         *
         * Une vidéo coupée par l'élève, de même, ne dit rien de sa caméra —
         * `cameraVerdict` rend ⚠️ dès `isVideoMuted`, avant même de regarder si
         * le flux marche (P-3925, cas vécu par Nada sur iPhone le 29/09) :
         * couper sa caméra effaçait le souvenir d'une caméra qui, elle,
         * fonctionnait très bien. `materialBilanForMemory` porte cette
         * exclusion (et reste la seule à la porter) : testable sans monter cet
         * écran, ce que `PreCallScreen` ne permet pas (P-3409).
         */
        if (shouldAlertTechnician(materialBilanForMemory(check, { isVideoMuted: props.isVideoMuted }))) {
            props.forgetDeviceCheck()
        }

        // L'étiquette suit le voyant le plus grave, elle n'est plus en dur :
        // une caméra refusée arrivait dans le canal annoncée comme un problème
        // de son.
        const category = alertCategory(check) ?? ISSUE_REPORT_CATEGORY.OTHER
        trackPrecall(PRECALL_EVENT.ALERT_SENT, { ...tracking, category })

        props.apiRequest(
            liveServiceV2.submitIssueReport,
            { id: liveSessionId },
            {
                category,
                description:
                    "Entrée au live sans que tout soit au vert, détectée par le test matériel d'entrée.",
                source: ISSUE_REPORT_SOURCE.PRECALL_CHECK,
                attempts: check.attempts,
                // Les quatre verdicts acquis — ceux-là mêmes qui ont déclenché
                // l'alerte. Sans eux le message ne pouvait rendre que la cause
                // du micro, quel que soit l'appareil réellement en défaut.
                verdicts: {
                    microphone: check.microphone,
                    speaker: check.speaker,
                    camera: check.camera,
                    network: check.network,
                },
                // Et la mesure brute à côté : la cause exacte par appareil,
                // pour que le technicien sache quel geste faire.
                diagnostics: check.diagnostics,
                platform: IS_WEB ? "web" : Platform.OS,
            }
        )
    }

    /**
     * Le filet ne vaut que pour l'élève.
     *
     * Le tuteur traverse le même écran — sa carte de séance l'y pousse — et le
     * filet ne le distinguait pas. Un tuteur au micro refusé postait donc un
     * signalement dont l'en-tête Slack annonce, en toutes lettres, que c'est
     * l'élève qui est entré sans matériel, avec un ping sur toute l'équipe.
     * Une alerte qui désigne la mauvaise personne, c'est le canal qu'on
     * apprend à ignorer.
     */
    const canAlertTechnician = hasRole(props.user, "student")
    // Qui est devant l'écran : « ton tuteur t'attend » ou « tes élèves t'attendent ».
    const audience = hasRole(props.user, "student") ? AUDIENCE.STUDENT : AUDIENCE.TUTOR
    const entryCopy = audienceCopy(audience)

    /*
     * L'équipe est prévenue ; l'élève, non.
     *
     * L'écran annonçait « un technicien va t'aider » et lui faisait attendre
     * quelqu'un. C'est une promesse que le produit ne peut pas tenir seul :
     * elle suppose une astreinte sur le canal, soirs et week-ends compris.
     * Une aide annoncée qui n'arrive pas vaut moins que pas d'aide annoncée —
     * surtout à un enfant qui a déjà un micro en panne.
     *
     * Le signalement part donc en silence, comme une information d'équipe. Ce
     * que l'élève lit reste vrai sans dépendre de personne : son micro ne
     * répond pas, il peut entrer quand même.
     */
    const handleJoinButtonClick = () => {
        // Tout voyant mesuré non vert prévient l'équipe, quel qu'il soit —
        // arbitrage du 26/08, rétabli le 07/09, borné aux mesures le 10/09.
        // La règle vit dans `deviceCheckRules`.
        if (canAlertTechnician && shouldAlertTechnician(check)) alertTechnician()
        if (guidedCheck) {
            trackPrecall(PRECALL_EVENT.JOIN, {
                ...tracking,
                all_done: !!check.allDone,
                microphone: check.microphone ?? null,
                speaker: check.speaker ?? null,
                camera: check.camera ?? null,
                network: check.network ?? null,
            })
        }
        proceedToJoin()
    }

    const handleSamOverlayClose = () => {
        setShowSamOverlay(false)
        // Once closed, don't reopen - next "Rejoindre" click joins directly
        setCheckpointDismissed(true)
    }

    const handleStartingBlockComplete = () => {
        setShowSamOverlay(false)
        joinSession()
    }

    const dimensions = useContext(DimensionsContext)

    const liveStudents = liveSession?.students?.filter(
        (user) => !user?.is_tutor && props.liveParticipants?.includes(user?.id)
    ) || []

    const userId = props.user?.id

    // useEffect(() => {
    //     if (
    //         onGoingConversation === true && props.lastPhotoFormId === formIds.CHECKPOINT) {
    //         amplitude.track("checkpoint.photo_workflow.complete")
    //         setIsModalVisible(true)
    //     }
    // }, [props.lastPhotoFormId, onGoingConversation])

    /*
     * Une seule demande, mais pas forcément au montage. Arrivé par un lien
     * direct, l'élève a sa séance — donc le drapeau checkpoint de la salle — une
     * seconde après l'affichage. Avec un effet à vide, la demande tirait sur un
     * drapeau encore faux, et Sam ne venait jamais dans ce parcours.
     */
    const askedPreCallRef = useRef(false)
    useEffect(() => {
        if (askedPreCallRef.current) return
        if (!hasRole(props.user, "student") || !isStartingBlockCheckpointEnabled) return
        askedPreCallRef.current = true
        props.apiRequest(liveService.getStudentPreCallData, { id: liveSessionId }, {})
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isStartingBlockCheckpointEnabled, props.user?.id])

    useApiStatus(
        liveService.getStudentPreCallData,
        null,
        false,
        (data) => {

            const context = data?.student?.has_study_program
                ? "starting_block_study_program"
                : "starting_block";

            if ((context === "starting_block_study_program" && !data?.live_occurrence?.chapter_id) ||
                (context === "starting_block" && !data?.live_occurrence?.subject_id)) {
                return;
            }
            if (data?.student?.has_checkpoint || data?.connection_duration > 5 * 60)
                return;

            if (props.createConversationRequest || props.createConversationSuccess || hasRequestedCheckpointRef.current) {
                return
            }

            props.apiRequest(
                liveService.createConversation,
                {},
                {
                    student_id: userId,
                    live_session_id: liveSessionId,
                    trigger_context: context
                }
            )
            hasRequestedCheckpointRef.current = true
        }
    )



    useApiStatus(
        liveService.createConversation,
        null,
        false,
        (data) => {
            // Only accept starting_block conversations in PreCallScreen
            // This prevents showing raise_hand/silent_mode checkpoints that may have errored
            const triggerContext = data?.conversation?.trigger_context
            if (triggerContext === "starting_block" || triggerContext === "starting_block_study_program") {
                setConversation(data)
            } else {
                console.warn("[PreCallScreen] Ignoring non-starting_block conversation:", triggerContext)
            }
        }
    )


    return (
        <View
            style={{
                flex: 1,
            }}
        >
            <HeaderContainerM>
                <View style={{ flexDirection: "row", alignItems: "center" }}>
                    <TitleM>LIVE</TitleM>
                    <IconImageM
                        source={logo}
                        style={{ alignSelf: "center", marginLeft: 6 }}
                        width={100}
                        height={(100 * 339) / 1024}
                    />
                    <TitleM> - {liveSession?.live_room?.name}</TitleM>
                </View>
                <IconButton
                    icon={"times"}
                    iconColor={theme.colors.primary}
                    size={32}
                    style={{
                        position: "absolute",
                        left: 4,
                    }}
                    onPress={() => {
                        navigation.goBack()
                    }}
                />
            </HeaderContainerM>
            <ScrollView
                contentContainerStyle={{
                    flexGrow: 1,
                    alignItems: "center",
                    width: dimensions?.width,
                    alignSelf: "center",
                    paddingBottom: 28,
                }}
                style={{ width: "100%" }}
            >
                {showJoinScreen ? (
                    <>
                                        <InformationBarComponent isSilentMode={false} />

                                        {/* L'apercu a rejoint la carte camera du parcours, sous
                                            forme de miniature : le conseil et l'image que l'eleve
                                            doit juger sont enfin cote a cote. */}
                                        {guidedCheck && liveStudents?.length > 0 && (
                                            <View style={{ marginTop: 14, alignItems: "center" }}>
                                                <AvatarGroupM
                                                    users={liveStudents}
                                                    size={26}
                                                    onPress={() => onAvatarPress(liveStudents)}
                                                />
                                            </View>
                                        )}

                                        {/* Le test est isolé : s'il tombe, l'élève perd sa
                                            vérification, pas son cours. Le bouton d'entrée est
                                            volontairement hors de ce périmètre. */}
                                        {!sessionKnown ? (
                                            <View style={[checkPanelStyle, { paddingVertical: 48, alignItems: "center" }]}>
                                                <ActivityIndicator color={theme.colors.primary} />
                                            </View>
                                        ) : guidedCheck ? (
                                        <PreCallCheckBoundary
                                            style={checkPanelStyle}
                                            // Le test ne finira jamais : on le déclare
                                            // terminé pour que l'entrée reprenne sa
                                            // forme de bouton. Aucun verdict n'est
                                            // remonté, donc rien ne part vers
                                            // l'équipe : on ne sait rien de son
                                            // matériel, et une alerte qui n'a rien
                                            // mesuré ne dit rien à personne.
                                            onFailed={() => setCheck({ attempts: [], allDone: true })}
                                        >
                                            <PreCallCheckPanel
                                                style={checkPanelStyle}
                                                isVideoMuted={props.isVideoMuted}
                                                onSetVideoMuted={(muted) => props.setMuteVideo(muted)}
                                                onStateChange={setCheck}
                                                audience={audience}
                                                // Le choix de périphérique survit à
                                                // l'écran : la salle le reprendra au
                                                // lieu de rouvrir celui du système.
                                                onDevicesChange={props.setPrecallDevices}
                                                // Ce que cet appareil a déjà
                                                // vérifié, pour ne pas le
                                                // redemander sans raison.
                                                verified={props.precallVerified}
                                                onVerified={props.rememberDeviceCheck}
                                                tracking={tracking}
                                            />
                                        </PreCallCheckBoundary>
                                        ) : (
                                            // Salle sans le drapeau : l'écran d'avant, tel quel.
                                            <LegacyPreCallCheck
                                                width={IS_WEB ? dimension.width / 2 : dimension?.width - 20}
                                                height={dimension.height / 2}
                                                isVideoMuted={props.isVideoMuted}
                                                onToggleVideo={() => props.setMuteVideo(!props.isVideoMuted)}
                                                ratio={props.ratioSaved}
                                                liveStudents={liveStudents}
                                            />
                                        )}
                                        <View
                                            style={{
                                                alignSelf: "center",
                                                // L'espace n'est réservé que si Sam est là.
                                                marginTop: showSam ? 88 : 22,
                                            }}
                                        >
                                            {/*
                                                Largeur explicite, et c'est ce qui manquait : sans
                                                elle, ce conteneur se réduisait à la taille de son
                                                contenu. Tant que le test n'est pas fini, ce contenu
                                                est un petit lien — Sam, ancré à droite, retombait
                                                donc en plein milieu, par-dessus le texte.
                                            */}
                                            <View
                                                style={{
                                                    position: "relative",
                                                    alignItems: "center",
                                                    width: columnWidth,
                                                }}
                                            >
                                                {/*
                                                    Sam n'accompagne que l'invitation à entrer, pas
                                                    la sortie discrète : posé au-dessus d'un lien
                                                    « entrer sans finir le test », il encourageait
                                                    gaiement à sauter la vérification.
                                                */}
                                                {showSam && (
                                                    <TouchableOpacity
                                                        onPress={handleJoinButtonClick}
                                                        style={{
                                                            position: "absolute",
                                                            // Juste au-delà du bord droit du bouton,
                                                            // assez haut pour ne pas mordre sur son
                                                            // libellé, qui est centré.
                                                            right: -8,
                                                            top: -104,
                                                            width: 116,
                                                            height: 116,
                                                            zIndex: 0,
                                                        }}
                                                    >
                                                        <Image
                                                            source={samHappy}
                                                            style={{ width: "100%", height: "100%" }}
                                                            resizeMode="contain"
                                                        />
                                                    </TouchableOpacity>
                                                )}
                                                {/* Le bouton reste atteignable à tout instant —
                                                    c'est le critère anti-dead-end : rien ne doit
                                                    jamais empêcher d'entrer en cours. Mais tant
                                                    que le test n'est pas fini, il se tait : posé
                                                    en plein milieu, il concurrençait l'étape en
                                                    cours et laissait croire qu'il fallait
                                                    choisir entre vérifier et entrer. */}
                                                {/* Discret pendant le test, plein au bilan. En
                                                    capitales soulignees il criait plus fort que
                                                    l'etape en cours, ce qui etait exactement
                                                    l'inverse de l'intention. */}
                                                {!sessionKnown ? null : allDone ? (
                                                    <FilledButtonM
                                                        label={entryCopy.enterNow}
                                                        style={{ alignSelf: "center", zIndex: 1, width: columnWidth }}
                                                        onPress={handleJoinButtonClick}
                                                        disabled={props.createConversationRequest || props.getStudentPreCallDataRequest}
                                                        loading={props.createConversationRequest || props.getStudentPreCallDataRequest}
                                                    />
                                                ) : (
                                                    // La sortie de l'écran, sous la carte : lisible en
                                                    // gris, jamais un bouton — on ne donne pas envie de
                                                    // sauter le test. Masquée tant que le checkpoint de
                                                    // Sam se prépare, comme le bouton est désactivé :
                                                    // un clic rapide entrait en séance sans Sam.
                                                    (props.createConversationRequest || props.getStudentPreCallDataRequest) ? null : (
                                                    <TouchableOpacity
                                                        onPress={handleJoinButtonClick}
                                                        hitSlop={12}
                                                        style={{ alignSelf: "center", zIndex: 1, paddingVertical: 8, paddingHorizontal: 8 }}
                                                    >
                                                        <TextM
                                                            color={theme.colors.grey}
                                                            fontSize={13.5}
                                                            fontWeight={"SemiBold"}
                                                            style={{ textDecorationLine: "underline" }}
                                                        >
                                                            {check.started ? entryCopy.enterSkip : entryCopy.enterBefore}
                                                        </TextM>
                                                    </TouchableOpacity>
                                                    )
                                                )}
                                            </View>
                                        </View>
                    </>
                ) : (
                    <BrowserChoiceScreen
                        width={dimensions?.width ?? 0}
                        onContinueHere={() => setShowJoinScreen(true)}
                        onOpenStore={() => {
                            // `storeLinks` existe pour ça : la résolution d'URL
                            // s'était recopiée d'écran en écran et les copies
                            // divergeaient sur les valeurs de repli. Écrite à
                            // la main ici, elle perdait le dernier filet — un
                            // `playStoreUrl` absent d'une build levait une
                            // TypeError —, laissait passer une chaîne vide
                            // venue de staticData, et ce point d'entrée
                            // n'apparaissait dans aucune mesure d'installation.
                            const store = detectStore()
                            trackStoreClick(store, "precall_browser_choice")
                            openStoreUrl(
                                resolveStoreUrl(store, {
                                    iosAppUrl: props.iosAppUrl,
                                    androidAppUrl: props.androidAppUrl,
                                })
                            )
                        }}
                    />
                )}

                <ModalM
                    visible={isModalOpen}
                    onDismiss={handleShowModal}
                    style={{
                        width: dimension.width > 800 ? null : "90%",
                        maxHeight: "80%",
                    }}
                >
                    <IconButton
                        icon={"times"}
                        iconColor={theme.colors.primary}
                        containerColor={theme.colors.transparent}
                        size={32}
                        style={{
                            position: "absolute",
                            left: 4,
                            top: 4,
                        }}
                        onPress={handleShowModal}
                    />
                    <DownloadAppCard
                        navigation={navigation}
                        style={{
                            marginTop: 50,
                        }}
                    />
                </ModalM>
            </ScrollView>

            {/* Sam Starting Block Overlay */}
            {showSamOverlay && conversation && (
                <Animated.View
                    entering={FadeIn.duration(300)}
                    exiting={FadeOut.duration(300)}
                    style={[
                        localStyles.samOverlayContainer,
                        {
                            width: '100%',
                            height: dimension?.height || '100%',
                            paddingTop: insets.top,
                        }
                    ]}
                >
                    {/* Background */}
                    <View style={localStyles.backdrop} />
                    <LinearGradient
                        colors={[
                            "#F8FAFC",
                            "#F1F5F9",
                            "#E2E8F0",
                        ]}
                        style={StyleSheet.absoluteFillObject}
                    />

                    {/* Content */}
                    <Animated.View
                        entering={SlideInUp.duration(500)}
                        exiting={SlideOutUp.duration(300)}
                        style={localStyles.contentContainer}
                    >
                        <ScrollView
                            contentContainerStyle={localStyles.scrollViewContent}
                            showsVerticalScrollIndicator={false}
                        >
                            <SamConversationBase
                                conversation={conversation}
                                context={conversation?.conversation?.trigger_context || "starting_block"}
                                onClose={handleSamOverlayClose}
                                isOverlay={false}
                                showInitialLoading={false}
                                onStartingBlockComplete={handleStartingBlockComplete}
                                setSuggestedActivity={props.setSuggestedActivity}
                                currentActivity={props.currentActivity}
                                renderCustomButtons={({ conversationData, isTextAnimationComplete, performClose }) => {
                                    const isStartingBlock =
                                        conversationData?.conversation?.trigger_context === "starting_block" ||
                                        conversationData?.conversation?.trigger_context === "starting_block_study_program"

                                    const hasNoResponses =
                                        !conversationData?.agent_message?.proposed_responses ||
                                        conversationData?.agent_message?.proposed_responses.length === 0

                                    const isNotOngoing = conversationData?.conversation?.status !== "ongoing"

                                    if (isStartingBlock && isTextAnimationComplete && isNotOngoing && hasNoResponses) {
                                        return (
                                            <Animated.View
                                                entering={BounceIn.delay(300).duration(600).springify()}
                                                style={localStyles.cestPartiContainer}
                                            >
                                                <FilledButtonM
                                                    label="C'est parti ! 🚀"
                                                    color={theme.colors.primary}
                                                    labelStyle={{
                                                        color: theme.colors.white,
                                                        fontSize: 16
                                                    }}
                                                    onPress={() => {
                                                        amplitude.track("sam_screen.starting_block.cest_parti.click")
                                                        PlaySoundM.play(require("../../../assets/sounds/select_002.mp3"))
                                                        performClose()
                                                        handleStartingBlockComplete()
                                                    }}
                                                    trackingId="starting_block_cest_parti"
                                                    style={localStyles.cestPartiButton}
                                                />
                                            </Animated.View>
                                        )
                                    }
                                    return null
                                }}
                            />
                        </ScrollView>
                    </Animated.View>
                </Animated.View>
            )}
        </View>
    )
}

// Local styles for Sam overlay
const localStyles = StyleSheet.create({
    samOverlayContainer: {
        position: 'absolute',
        top: 0,
        left: 0,
        zIndex: 9999,
        elevation: 9999,
    },
    backdrop: {
        ...StyleSheet.absoluteFillObject,
        backgroundColor: 'rgba(0, 0, 0, 0.3)',
    },
    contentContainer: {
        flex: 1,
        zIndex: 2,
    },
    scrollViewContent: {
        flexGrow: 1,
        justifyContent: 'flex-start',
        alignItems: 'center',
        // iOS needs more paddingTop to account for notch, Web/Android need less
        paddingTop: Platform.OS === 'ios' ? 60 : 20,
        paddingBottom: 140,
        paddingHorizontal: 20,
    },
    cestPartiContainer: {
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: 28,
    },
    cestPartiButton: {
        alignSelf: 'stretch',
    },
})
const mapStateToProps = (state) => {
    return {
        liveSessions: state.data.currentUser?.object?.live_sessions,
        liveSessionId: state.liveMode?.liveSessionId,
        isAudioMuted: state.liveMode?.isAudioMuted,
        isVideoMuted: state.liveMode?.isVideoMuted,
        user: state.data.currentUser?.object,
        imagePaddingSaved: state.camera?.imagePadding,
        androidAppUrl: state.data.staticData?.android_app_url,
        liveParticipants: state.liveMode?.liveParticipants,
        iosAppUrl: state.data.staticData?.ios_app_url,
        hasMobileSessionSuccess: state.api.hasMobileSession?.success,
        ongoingCheckpointIds: state.liveMode?.ongoingCheckpointIds,
        createConversationRequest: state.api.createConversation?.request,
        createConversationSuccess: state.api.createConversation?.success,
        getStudentPreCallDataRequest : state.api.getStudentPreCallData?.request,
        // Le souvenir du dernier test matériel réussi sur cet appareil.
        precallVerified: state.precallMemory?.verified,
        // Le repli sans drapeau garde l'aperçu d'avant, et son ratio.
        ratioSaved: state.camera?.ratio,
    }
}

const mapDispatchToProps = {
    apiRequest,
    apiIdle,
    setCallPortal,
    setMuteAudio,
    setMuteVideo,
    setIsLiveModeEnabled,
    setIssoundMode,
    setPrecallDevices,
    setSuggestedActivity,
    rememberDeviceCheck,
    forgetDeviceCheck,
}
export default connect(mapStateToProps, mapDispatchToProps)(PreCallScreen)
